import { useEffect, useMemo, useState, type FormEvent } from 'react';
import api, { ApiError } from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconView } from '../components/icons';
import { Modal } from '../components/Modal';
import Pagination from '../components/Pagination';
import { useAuth } from '../auth/useAuth';
import { labelFor, listOptions, useConfig } from '../context/ConfigContext';
import { formatMoney } from '../utils/format';
import usePaginatedList from '../hooks/usePaginatedList';

interface BenefitClaimRow {
  id: number;
  reference: string;
  claim_type: string;
  member: { id: number; user: { first_name: string; last_name: string } | null };
  calculated_entitlement: string | null;
  approved_amount: string | null;
  status: string;
}

interface MemberOption {
  id: number;
  user: { first_name: string; last_name: string } | null;
}

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  pending_approval: 'Pending Approval',
  rejected: 'Rejected',
  approved: 'Approved',
  payment_generated: 'Payment Generated',
  disbursed: 'Disbursed',
  returned_to_suspense: 'Returned to Suspense',
  reversed: 'Reversed',
};

const STATUS_BADGE: Record<string, string> = {
  draft: 'badge-neutral',
  submitted: 'badge-scheduled',
  pending_approval: 'badge-scheduled',
  rejected: 'badge-urgent',
  approved: 'badge-active',
  payment_generated: 'badge-active',
  disbursed: 'badge-active',
  returned_to_suspense: 'badge-urgent',
  reversed: 'badge-urgent',
};

const emptyForm = { claim_type: '', member_id: '', claimed_amount: '' };

export function BenefitClaims() {
  const { can } = useAuth();
  const { settings, lists } = useConfig();
  const [status, setStatus] = useState('');
  const [claimType, setClaimType] = useState('');

  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [members, setMembers] = useState<MemberOption[]>([]);

  const canCreate = can('memberships.benefits.create');

  const filters = useMemo(() => ({ status: status || undefined, claim_type: claimType || undefined }), [status, claimType]);
  const { data, loading, error, setPage, reload } = usePaginatedList<BenefitClaimRow>('/benefit-claims', filters);

  const fmtMoney = (n: string | null) => (n === null ? '—' : formatMoney(Number(n), settings.currency));

  useEffect(() => {
    if (!canCreate) return;
    api
      .get<{ data: MemberOption[] }>('/members', { params: { per_page: 100 } })
      .then((res) => setMembers(res.data.data))
      .catch(() => setMembers([]));
  }, [canCreate]);

  const memberName = (m: MemberOption) => (m.user ? `${m.user.first_name} ${m.user.last_name}` : `Member #${m.id}`);

  const openAdd = () => {
    setForm(emptyForm);
    setErrors({});
    setAdding(true);
  };

  const submitAdd = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await api.post('/benefit-claims', {
        claim_type: form.claim_type,
        member_id: form.member_id,
        claimed_amount: form.claimed_amount ? Number(form.claimed_amount) : undefined,
      });
      setAdding(false);
      reload();
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  return (
    <>
      <header className="page-header">
        <h1>Benefit Claims</h1>
        {canCreate && (
          <button type="button" className="btn btn-primary" onClick={openAdd}>
            + Capture Claim
          </button>
        )}
      </header>

      <div className="toolbar">
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {Object.entries(STATUS_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <select value={claimType} onChange={(e) => setClaimType(e.target.value)}>
          <option value="">All claim types</option>
          {listOptions(lists, 'benefit_claim_type').map((item) => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </select>
      </div>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Reference</th>
                <th>Member</th>
                <th>Claim Type</th>
                <th>Entitlement</th>
                <th>Approved</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="muted center">
                    Loading…
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={7} className="error center">
                    {error}
                  </td>
                </tr>
              ) : data && data.data.length > 0 ? (
                data.data.map((claim) => (
                  <tr key={claim.id}>
                    <td className="muted">{claim.reference}</td>
                    <td>
                      <strong>{claim.member.user ? `${claim.member.user.first_name} ${claim.member.user.last_name}` : `Member #${claim.member.id}`}</strong>
                    </td>
                    <td>{labelFor(lists, 'benefit_claim_type', claim.claim_type)}</td>
                    <td>{fmtMoney(claim.calculated_entitlement)}</td>
                    <td>{fmtMoney(claim.approved_amount)}</td>
                    <td>
                      <span className={`badge ${STATUS_BADGE[claim.status] ?? 'badge-neutral'}`}>
                        {STATUS_LABELS[claim.status] ?? claim.status}
                      </span>
                    </td>
                    <td className="right">
                      <IconButton icon={IconView} label="View" to={`/memberships/benefits/claims/${claim.id}`} />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="muted center">
                    No benefit claims yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination meta={data} onPage={setPage} />
      </div>

      {adding && (
        <Modal title="Capture a benefit claim" onClose={() => setAdding(false)}>
          <form onSubmit={submitAdd} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <label className="span-2">
              Member *
              <select value={form.member_id} onChange={(e) => setForm({ ...form, member_id: e.target.value })} required>
                <option value="">Select…</option>
                {members.map((m) => (
                  <option key={m.id} value={m.id}>
                    {memberName(m)}
                  </option>
                ))}
              </select>
              {errors.member_id && <small className="error">{errors.member_id[0]}</small>}
            </label>
            <label className="span-2">
              Claim type *
              <select value={form.claim_type} onChange={(e) => setForm({ ...form, claim_type: e.target.value })} required>
                <option value="">Select…</option>
                {listOptions(lists, 'benefit_claim_type').map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
              {errors.claim_type && <small className="error">{errors.claim_type[0]}</small>}
            </label>
            <label className="span-2">
              Claimed amount
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.claimed_amount}
                onChange={(e) => setForm({ ...form, claimed_amount: e.target.value })}
              />
              <small className="muted">Informational only — the actual entitlement is calculated on submission.</small>
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setAdding(false)}>
                Cancel
              </button>
              <button className="btn btn-primary">Save as Draft</button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
